import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';

import SortableItem from './SortableItem';
import { insertionCollision } from './insertionCollision';
import useStyles from './useStyles';

const getDropIndex = (from, target, position) => {
  const slot = target + (position === 'after' ? 1 : 0);
  return from < slot ? slot - 1 : slot;
};

/** Controlled reorder list; `onMove(from, to)` follows `arrayMove`, so a Formik FieldArray `move` fits directly. */
const SortableList = ({ items, getId, renderItem, onMove, disabled }) => {
  const classes = useStyles();
  const [activeId, setActiveId] = useState(null);
  const [caret, setCaret] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { delay: 250 } }),
    useSensor(TouchSensor)
  );

  const ids = items.map(getId);
  const activeIndex = ids.indexOf(activeId);

  const reset = () => {
    setActiveId(null);
    setCaret(null);
  };

  const handleDragMove = ({ active, over, collisions }) => {
    const position = over && over.id !== active.id
      ? collisions?.find(({ id }) => id === over.id)?.data?.position
      : null;
    const next = position ? { id: over.id, position } : null;
    setCaret((prev) => (prev?.id === next?.id && prev?.position === next?.position ? prev : next));
  };

  const handleDragEnd = ({ active }) => {
    const from = ids.indexOf(active.id);
    const target = caret ? ids.indexOf(caret.id) : -1;
    if (from !== -1 && target !== -1) {
      const to = getDropIndex(from, target, caret.position);
      if (to !== from) onMove(from, to);
    }
    reset();
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={insertionCollision}
      onDragStart={({ active }) => setActiveId(active.id)}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <SortableContext items={ids}>
        <ul className={classes.list}>
          {items.map((item, index) => (
            <SortableItem
              key={ids[index]}
              id={ids[index]}
              disabled={disabled}
              caret={caret?.id === ids[index] ? caret.position : null}
            >
              {renderItem(item, index)}
            </SortableItem>
          ))}
        </ul>
      </SortableContext>
      <DragOverlay>
        {activeIndex !== -1 ? (
          <div className={classes.overlay}>{renderItem(items[activeIndex], activeIndex)}</div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

SortableList.propTypes = {
  items: PropTypes.array.isRequired,
  getId: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SortableList.defaultProps = {
  disabled: false,
};

export default SortableList;
